/**
 * Identifies which AIM sub-app/context a view or request is scoped to, used to vary API queries and UI behavior by context.
 */
export enum AppContexts {
  AIM = '',
  PotentialAcquisitions = 'PotentialAcquisitions',
  CurrentAssets = 'CurrentAssets',
  StateAssets = 'StateAssets',
  CountrySummary = 'CountrySummary',
  Analytics = 'Analytics'
}

/**
 * Top-level status buckets used to filter/group acquisitions across the opportunity and transaction lifecycle.
 */
export enum AcquisitionStatus {
  opportunities = "Opportunities",
  active = "Transactions",
  completed = "Completed",
  historic = "Historic",
  pendingApprovals = "PendingApprovals",
  completedApprovals = "CompletedApprovals"
}

/**
 * An AIM business division, including the price threshold above which a deal is flagged as high-value.
 */
export interface Division {
  aimDivision: string;
  description: string;
  highValueThreshold: number;
}

/**
 * Lookup entry describing how an acquired property is managed (e.g. operated, leased).
 */
export interface ManagementType {
  aimManagementType: string;
  description: string;
  active: boolean;
  /** Optional GIS label mapping this management type to the property table's `managementType` value. Nullable. */
  gisLabel?: string | null;
}

/**
 * Describes the current user's AIM access: whether they're an AIM/FSM user, their employee id, assigned AIM roles, and target tenant environment.
 */
export interface Capabilities {
  actor: string;
  aimUser: boolean;
  employee: number;
  fsmUser: boolean;
  aimRoles: string[];
  tenant: 'trn' | 'prd' | string;
}
/**
 * Lookup entry describing the type of a transaction (e.g. purchase, sale).
 */
export interface TransactionType {
  aimTransactionType: string;
  description: string;
}
/**
 * Lookup entry describing the sale method used for a transaction (e.g. auction, private sale).
 */
export interface SaleType {
  aimSaleType: string;
  description: string;
}

/**
 * Lookup entry defining a document access-restriction tier and its relative ordering.
 */
export interface DocumentSecurityLevel {
  aimDocumentSecurityLevel: string;
  description: string;
  order: string;
}

/**
 * Lookup entry describing a stewardship role/category assignable within a division.
 */
export interface Stewardship {
  aimStewardship: string;
  description: string;
}

/**
 * Currency lookup entry with ISO code/number and decimal-precision metadata for formatting monetary values.
 */
export interface Currency {
  currency: string;
  description: string;
  numberOfDecimals: number;
  isoCode: string;
  isoCodeNumber: number;
}

/**
 * Represents a full user profile record from the AIM system, including employment details, department/role flags, and whether the employee is assignable to AIM tasks.
 */
export interface Employee {
  employee: number;
  name: string;
  workEmail: string;
  job: string;
  position: string;
  jobTitle: string;
  jobLocation: string;
  jobLevel: string;
  jobCategory:
    | 'LAND_LEG'
    | 'ACCT_FIN'
    | 'OPS'
    | 'EXEC'
    | 'IT'
    | ''
    | 'SLS_MKTG'
    | 'HR'
    | 'ADMIN'
    | 'SOURCING'
    | string;
  partOfC: boolean;
  partOfHDQ: boolean;
  partOfLP: boolean;
  partOfPP: boolean;
  partOfRC: boolean;
  isCEO: boolean;
  isCFO: boolean;
  isFinance: boolean;
  isLand: boolean;
  isLegal: boolean;
  isProduction: boolean;
  active: boolean;
  aimAssignable: boolean;
}

/**
 * A lookup entry mapping an employee to a division stewardship role, including their name, title, email, and actor.
 */
export interface DivisionSteward {
  aimDivisionSteward: string;
  aimDivision: string;
  aimStewardship: string;
  description: string;
  employee: number;
  name: string;
  jobTitle: string;
  email: string;
  actor: string;
}

/**
 * A lookup entry describing a transaction role, including its code and description.
 */
export interface TransactionRole {
  aimTransactionRole: string;
  description: string;
}

/**
 * A lookup entry for a country, including its code and display name.
 */
export interface AimCountry {
  country: string;
  name: string;
  active: boolean;
}

/**
 * A lookup entry for a state or province, linked to its parent country.
 */
export interface AimStateProvince {
  country: string;
  stateProvince: string;
  name: string;
}

/**
 * A user note with text, tags, and creation/modification metadata.
 */
export interface AimNote {
  aimNote: string;
  id: number;
  created: string;
  modified: string;
  deleted: boolean;
  title: string;
  message: string;
  email: string;
  name: string;
  tags: string[];
}

/**
 * Identifies which AIM module or context a request or view belongs to. Each member maps to a string constant used to scope API queries and UI behavior.
 */
export enum ModuleIdentifier {
  aimOpportunity,
  aimTransactionTeam,
  aimId
}

/**
 * A versioned release-notes structure containing the current version and a list of release entries.
 */
export interface ReleaseNotes {
  currentVersion: string;
  releases: ReleaseNote[];
}

/**
 * A single release notes entry for a specific version, including version identifiers, release date, and description.
 *
 * The `released` and `releaseDate`/`startDate` fields are nullable (mapped from C# `bool?`/`string?`). `released: true` indicates the version has shipped.
 *
 * @example
 * Aims at a v2.1.0 entry with a known release date.
 * A draft entry where released is null and releaseDate is null.
 */
export interface ReleaseNote {
  versionId: string;
  versionName: string;
  released: boolean | null; // C# bool? -> boolean | null
  releaseDate: string | null; // C# string? -> string | null
  startDate: string | null;   // C# string? -> string | null
  description: string;
}
